import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Money, ListShell } from "@/components/ui-veda/Primitives";
import { TID } from "@/constants/testIds";

const STATUSES = ["placed", "shipped", "delivered", "cancelled"];

function formatDT(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

export default function AdminOrders() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    async function load() {
        setLoading(true);
        try {
            const { data } = await api.get("/orders");
            setItems(data);
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        load();
    }, []);

    async function updateStatus(o, status) {
        setItems((prev) =>
            prev.map((x) => (x.order_id === o.order_id ? { ...x, status } : x)),
        );
        try {
            await api.patch(`/orders/${o.order_id}/status`, null, {
                params: { status },
            });
            toast.success(`Order marked as ${status}.`);
        } catch {
            toast.error("Update failed.");
            load();
        }
    }

    let body;
    if (loading || items.length === 0) {
        body = (
            <ListShell
                loading={loading}
                isEmpty={!loading && items.length === 0}
                emptyMessage="No orders yet."
            />
        );
    } else {
        body = (
            <ul className="divide-y divide-veda-divider">
                {items.map((o) => (
                    <li key={o.order_id} className="py-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-semibold text-veda-onSurface">
                                    #{o.order_id.slice(-8)} · {o.customer_name}
                                </p>
                                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-veda-muted">
                                    {formatDT(o.created_at)} · {o.payment_method}
                                </p>
                            </div>
                            <p className="text-sm font-semibold text-veda-onSurface">
                                <Money value={o.total} />
                            </p>
                        </div>
                        <p className="mt-1 text-xs text-veda-muted">
                            Ship to: {o.address} · {o.phone}
                        </p>
                        <ul className="mt-2 space-y-1 text-xs text-veda-muted">
                            {o.items.map((it, idx) => (
                                <li key={`${it.product_id || "item"}-${idx}`}>
                                    {it.quantity} × {it.name} —{" "}
                                    <Money value={it.price * it.quantity} />
                                </li>
                            ))}
                        </ul>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {STATUSES.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => updateStatus(o, s)}
                                    className={`rounded-pill border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${
                                        o.status === s
                                            ? "border-veda-inverse bg-veda-inverse text-veda-onInverse"
                                            : "border-veda-border text-veda-onSurface"
                                    }`}
                                >
                                    {s}
                                </button>
                            ))}
                        </div>
                    </li>
                ))}
            </ul>
        );
    }

    return (
        <div data-testid={TID.adminOrders} className="flex flex-col gap-3">
            {body}
        </div>
    );
}
